import "@afassoftware/components/css/afas-theme.css";
import "@afassoftware/components/css/afas-theme-dark.css";
import "@afassoftware/components/css/application.css";
import {
  AfasAlert,
  AfasButton,
  AfasButtonGroup,
  AfasChatOverview,
  AfasCheckbox,
  AfasContainerHeader,
  AfasDesignerLayout,
  AfasDialog,
  AfasDropdownButton,
  AfasFieldRow,
  AfasFile,
  AfasFileGroup,
  AfasGrid,
  AfasHeader,
  AfasIcon,
  AfasLabel,
  AfasLoadingSpinner,
  AfasCommentForm,
  AfasMarkdown,
  AfasMessage,
  AfasMiniFileInput,
  AfasMenu,
  AfasMenuDivider,
  AfasMenuItem,
  AfasPopover,
  AfasSearchInput,
  AfasSelect,
  AfasSkeleton,
  AfasSwitch,
  AfasTag,
  AfasTextInput,
  AfasTextareaInput,
  AfasTooltip,
  AfasUrlInput,
  setLocale,
} from "@afassoftware/components";

import "./styles/afas-base.css";
import "./global.css";
import "./styles/authorit-article.css";
import "./styles/workspace.css";
import "./components/jonas-chat-appbar.js";
import "./components/jonas-chat-app.js";
import "./components/jonas-chat-shell.js";
import "./components/jonas-chat-thread.js";
import "./components/jonas-chat-composer.js";
import "./components/jonas-status.js";
import "./components/jonas-source-panel.js";
import "./components/jonas-source-pill.js";
import "./components/jonas-chat-message.js";
import "./components/jonas-image-dialog.js";
import "./components/jonas-home-prompts.js";
import "./components/jonas-suggestions.js";
import "./components/jonas-appbar-status.js";
import "./components/jonas-home-tile-dialog.js";
import "./components/jonas-home-tile-manager.js";
import "./components/jonas-cms-login.js";
import "./components/jonas-cms-logout.js";
import "./components/jonas-integration.js";
import { resolveAfasLocale } from "./afas-locale.js";
import { parseInitialState } from "./config.js";
import { bootstrapEmbedMode } from "./embed.js";
import { installPrintLightTheme } from "./print-theme.js";
import type { InitialWorkspaceState } from "./types.js";

type AfasElementConstructor = CustomElementConstructor & {
  NAME?: string;
  dependencies?: AfasElementConstructor[];
};

registerAfasElements([
  AfasAlert,
  AfasButton,
  AfasButtonGroup,
  AfasChatOverview,
  AfasCheckbox,
  AfasContainerHeader,
  AfasDesignerLayout,
  AfasDialog,
  AfasDropdownButton,
  AfasFieldRow,
  AfasFile,
  AfasFileGroup,
  AfasIcon,
  AfasLabel,
  AfasLoadingSpinner,
  AfasCommentForm,
  AfasMarkdown,
  AfasMessage,
  AfasMiniFileInput,
  AfasMenu,
  AfasMenuDivider,
  AfasMenuItem,
  AfasPopover,
  AfasSearchInput,
  AfasSelect,
  AfasSkeleton,
  AfasSwitch,
  AfasTag,
  AfasTextInput,
  AfasTextareaInput,
  AfasTooltip,
  AfasUrlInput,
]);

// Always print in the light theme (dark prints as washed-out grey on paper).
// Covers the browser's own Ctrl+P for every mode; the article print also forces
// it synchronously via the same module.
installPrintLightTheme();

applyAfasLocale();

bootstrapWorkspace();

// AFAS components localize their own strings with @lit/localize. Our own labels come from
// SharedResources per culture, but without this the component-owned strings stay in the
// package's source language on every Help center. Since 5.x that source locale is the
// placeholder `zxx` and Dutch is a catalog of its own, so nl needs the call as much as en
// and fr do. Every catalog is already in this bundle, so selecting one costs no download.
//
// This cannot run before the components first render: the @afasComponent decorator
// registers every element at import time, and setLocale is asynchronous. Components
// re-render on the locale switch, which since 5.x also covers the icon-only tooltip.
function applyAfasLocale(): void {
  const locale = resolveAfasLocale(document.documentElement.lang);
  // A catalog that fails to load leaves the source strings in place, which is always
  // better than breaking the bundle bootstrap over a translation.
  void setLocale(locale).catch(() => undefined);
}

function registerAfasElements(elements: AfasElementConstructor[]): void {
  const seen = new Set<AfasElementConstructor>();
  for (const element of elements) registerAfasElement(element, seen);
}

function registerAfasElement(
  element: AfasElementConstructor,
  seen: Set<AfasElementConstructor>,
): void {
  if (seen.has(element)) return;
  seen.add(element);

  for (const dependency of element.dependencies ?? []) {
    registerAfasElement(dependency, seen);
  }

  const tagName = element.NAME;
  if (!tagName || customElements.get(tagName)) return;
  customElements.define(tagName, element);
}

// Embed mode renders no <help-jonas-chat-app>, so nothing chat-related boots on
// its own. Read the server initial-state island and, only for embed, mount the
// article-only view. Full and prompt modes render <help-jonas-chat-app>, which
// self-boots in its own connectedCallback, so they are intentionally left alone.
function bootstrapWorkspace(): void {
  const island = document.getElementById("chat-workspace-initial-state");
  if (!island?.textContent) return;

  let state: InitialWorkspaceState;
  try {
    state = parseInitialState(island.textContent);
  } catch {
    return;
  }

  if (state.mode !== "embed") return;

  bootstrapEmbedMode(document, state.selectedSourceUrl);
}
